import React, { useEffect, useRef } from 'react';
import '../Style/Service.css';
import { useLanguage } from "../Context/Languagecontext";

import img1 from '../Assets/Service1.jpg';
import img2 from '../Assets/Service2.jpg';
import img3 from '../Assets/Service3.jpg';
import img4 from '../Assets/Service5.jpg';

function renderHeadline(headline, italicWord) {
  if (!italicWord) return headline;
  const parts = headline.split(italicWord);
  return (
    <>
      {parts[0]}
      <em>{italicWord}</em>
      {parts[1]}
    </>
  );
}

export default function Service() {
  const sectionRef = useRef(null);
  const { t, lang } = useLanguage();
  const s = t.service;

  // Structural dynamic mapping using data fetched from context
  const CARDS = [
    {
      id: 1,
      mod: 'sv-card--1 sv-card--img',
      img: img1,
      alt: lang === 'ta' ? 'உள்ளூர் நிகழ்வில் சமூகக் கூட்டம்' : 'Community gathering at a local event',
      tag: s?.cards?.card1?.tag || '',
      headline: s?.cards?.card1?.headline,
      headlineItalic: s?.cards?.card1?.headlineItalic,
      sub: s?.cards?.card1?.sub,
    },
    {
      id: 2,
      mod: 'sv-card--2 sv-card--img',
      img: img2,
      alt: lang === 'ta' ? 'உள்ளூர் பட்டறையில் கைவினைஞர்கள் வேலை செய்கிறார்கள்' : 'Artisans at work in a local workshop',
      tag: s?.cards?.card2?.tag,
      headline: s?.cards?.card2?.headline,
      headlineItalic: s?.cards?.card2?.headlineItalic,
      sub: s?.cards?.card2?.sub,
    },
    {
      id: 3,
      mod: 'sv-card--3 sv-card--img',
      img: img3,
      alt: lang === 'ta' ? 'சமூகத் தலைவருடன் நேர்காணல்' : 'Interview with a community leader',
      tag: s?.cards?.card3?.tag,
      headline: s?.cards?.card3?.headline,
      headlineItalic: s?.cards?.card3?.headlineItalic,
      sub: s?.cards?.card3?.sub,
    },
    {
      id: 4,
      mod: 'sv-card--4 sv-card--quote',
      quote: s?.cards?.card4?.quote,
      attr: s?.cards?.card4?.attr,
    },
    {
      id: 5,
      mod: 'sv-card--5 sv-card--img',
      img: img4,
      alt: lang === 'ta' ? 'அதிகாலையில் படகில் மீனவர்கள்' : 'Fishermen on a boat at dawn',
      tag: s?.cards?.card5?.tag,
      headline: s?.cards?.card5?.headline,
      headlineItalic: s?.cards?.card5?.headlineItalic,
      sub: s?.cards?.card5?.sub,
    },
  ];

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const animated = section.querySelectorAll(
      '.service-header__eyebrow, .service-header__title, .service-header__desc, .sv-card'
    );

    const reset = () => {
      animated.forEach((el) => {
        el.style.animationName = 'none';
        el.style.opacity = '';
      });
    };

    const trigger = () => {
      animated.forEach((el) => {
        void el.offsetWidth;
        el.style.animationName = '';
      });
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          reset();
          requestAnimationFrame(() => requestAnimationFrame(trigger));
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className={`service-section${lang === "ta" ? " service-section--tamil" : ""}`}
      ref={sectionRef}
      aria-label={lang === 'ta' ? 'வணிகத்தைத் தாண்டிய சேவை' : 'Service Beyond Business'}
    >
      <header className="service-header">
        <div className="service-header__left">
          <p className="service-header__eyebrow">{s?.eyebrow}</p>
          <h2 className="service-header__title">
            {lang === 'ta' ? (
              <>வணிகத்தைத் <em>தாண்டிய</em><br />சேவை</>
            ) : (
              <>Service <em>Beyond</em><br />Business</>
            )}
          </h2>
        </div>
        <p className="service-header__desc">{s?.desc}</p>
      </header>

      <div className="service-grid">
        {CARDS.map((card) => {
          if (card.quote) {
            return (
              <article key={card.id} className={`sv-card ${card.mod}`}>
                <span className="sv-quote__mark" aria-hidden="true">&ldquo;</span>
                <p className="sv-quote__text">{card.quote}</p>
                <p className="sv-quote__attr">{card.attr}</p>
              </article>
            );
          }

          return (
            <article key={card.id} className={`sv-card ${card.mod}`}>
              <img
                className="sv-card__img"
                src={card.img}
                alt={card.alt}
                loading="lazy"
              />
              <div className="sv-card__overlay" aria-hidden="true" />
              <div className="sv-card__content">
                {card.id === 1 ? (
                  <>
                    <div className="sv-card__top-label">
                      <span className="sv-card__index">{card.index}</span>
                      {card.tag && <span className="sv-card__tag">{card.tag}</span>}
                    </div>
                    <div>
                      <div className="sv-card__rule" aria-hidden="true" />
                      <h2 className="sv-card__headline">
                        {renderHeadline(card.headline, card.headlineItalic)}
                      </h2>
                      {card.sub && <p className="sv-card__sub">{card.sub}</p>}
                    </div>
                  </>
                ) : (
                  <div>
                    {card.tag && <span className="sv-card__tag">{card.tag}</span>}
                    <div className="sv-card__rule" aria-hidden="true" />
                    <h3 className="sv-card__headline">
                      {renderHeadline(card.headline, card.headlineItalic)}
                    </h3>
                    {card.sub && <p className="sv-card__sub">{card.sub}</p>}
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}